import apiClient from "./config";
import { ECOMMERCE_ENDPOINTS, DASHBOARD_STATS, DASHBOARD_REVENUE_CHART, DASHBOARD_BEST_SELLING, DASHBOARD_NEW_SELLERS } from "./endpoints";

export const getDashboardStats = () => apiClient.get(DASHBOARD_STATS).then(r => r?.data || r);

export const getDashboardRevenueChart = (period = "all") =>
  apiClient.get(`${DASHBOARD_REVENUE_CHART}?period=${encodeURIComponent(period)}`).then((r) => r?.data || r);

export const getDashboardBestSelling = (params = { limit: 5 }) => {
  let query = '';
  if (typeof params === 'number') {
    query = `limit=${params}`;
  } else if (params) {
    query = new URLSearchParams(params).toString();
  }
  return apiClient.get(`${DASHBOARD_BEST_SELLING}?${query}`).then((r) => r?.data || r);
};

export const getDashboardNewSellers = (limit = 5) =>
  apiClient.get(`${DASHBOARD_NEW_SELLERS}?limit=${limit}`).then((r) => r?.data || r);

// Product Reviews
export const getProductReviews = (params) => apiClient.get(ECOMMERCE_ENDPOINTS.GET_PRODUCT_REVIEWS, params).then(r => r?.data || r);
export const deleteProductReview = (id) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_PRODUCT_REVIEW + '/' + id);

// Products
export const getProducts = (params = {}) => apiClient.get(ECOMMERCE_ENDPOINTS.GET_PRODUCTS, params);
export const deleteProducts = (product) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_PRODUCT + '/' + product);
export const addNewProduct = (product) => {
  // If product contains formData, use it directly (for file uploads)
  if (product.formData) {
    return apiClient.createWithFormData(ECOMMERCE_ENDPOINTS.ADD_NEW_PRODUCT, product.formData);
  }
  return apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_PRODUCT, product);
};
export const updateProduct = (product) => {
  const productId = product._id || product.id;
  // If product contains formData, use it directly (for file uploads)
  if (product.formData) {
    return apiClient.updateWithFormData(ECOMMERCE_ENDPOINTS.UPDATE_PRODUCT + '/' + productId, product.formData);
  }
  return apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_PRODUCT + '/' + productId, product);
};

// Orders
export const getOrders = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_ORDERS).then(r => r.data || r);
export const getOrder = (orderNumber) => apiClient.get(ECOMMERCE_ENDPOINTS.GET_ORDER + '/' + orderNumber).then(r => r.data || r);
export const addNewOrder = (order) => apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_ORDER, order);
export const updateOrder = (orderNumber, data) => apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_ORDER + '/' + orderNumber, data);
export const deleteOrder = (orderId) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_ORDER + '/' + orderId);
// Settings (Stripe)
export const getSettings = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_SETTINGS);
export const updateSetting = (key, value) => apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_SETTING + '/' + key, { value });

// Customers
export const getCustomers = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_CUSTOMERS);
export const addNewCustomer = (customer) => apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_CUSTOMER, customer);
export const updateCustomer = (customer) => apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_CUSTOMER + '/' + customer._id, customer);
export const deleteCustomer = (customer) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_CUSTOMER + '/' + customer);

// Sellers
export const getSellers = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_SELLERS);
export const getSeller = (sellerId) => apiClient.get(`${ECOMMERCE_ENDPOINTS.GET_SELLER}/${sellerId}`);
/** Logged-in user's seller row (same DB row as admin /sellers — bank details, etc.) */
export const getSellerMe = () =>
  apiClient.get(ECOMMERCE_ENDPOINTS.GET_SELLER_ME).then((r) => (r && "data" in r ? r.data : r));
export const addNewSeller = (seller) => {
  // If seller contains formData, use it directly (for file uploads)
  if (seller.formData) {
    return apiClient.createWithFormData(ECOMMERCE_ENDPOINTS.ADD_NEW_SELLER, seller.formData);
  }
  return apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_SELLER, seller);
};
export const updateSeller = (seller) => {
  const sellerId = seller._id || seller.id;
  // If seller contains formData, use it directly (for file uploads)
  if (seller.formData) {
    return apiClient.updateWithFormData(`${ECOMMERCE_ENDPOINTS.UPDATE_SELLER}/${sellerId}`, seller.formData);
  }
  return apiClient.update(`${ECOMMERCE_ENDPOINTS.UPDATE_SELLER}/${sellerId}`, seller);
};
export const deleteSeller = (sellerId) => apiClient.delete(`${ECOMMERCE_ENDPOINTS.DELETE_SELLER}/${sellerId}`);

// Seller withdrawal requests (admin + seller)
export const getSellerWithdrawals = () =>
  apiClient.get(ECOMMERCE_ENDPOINTS.SELLER_WITHDRAWALS).then((r) => {
    if (Array.isArray(r?.data)) return r.data;
    if (Array.isArray(r)) return r;
    return [];
  });
export const getSellerWithdrawal = (id) =>
  apiClient.get(`${ECOMMERCE_ENDPOINTS.SELLER_WITHDRAWALS}/${id}`).then((r) => r?.data ?? r);
export const patchSellerWithdrawal = (id, data) =>
  apiClient.update(`${ECOMMERCE_ENDPOINTS.SELLER_WITHDRAWALS}/${id}`, data).then((r) => r?.data ?? r);
export const postSellerWithdrawal = (data) =>
  apiClient.create(ECOMMERCE_ENDPOINTS.SELLER_WITHDRAWALS, data).then((r) => r?.data ?? r);
export const suspendSeller = (sellerId, status) => apiClient.update(`${ECOMMERCE_ENDPOINTS.SUSPEND_SELLER}/${sellerId}/suspend`, { status });

// Categories (Ecommerce/Product Categories)
export const getEcommerceCategories = (params = {}) => apiClient.get(ECOMMERCE_ENDPOINTS.GET_CATEGORIES, params);
export const addNewEcommerceCategory = (category) => {
  if (category.formData) {
    return apiClient.createWithFormData(ECOMMERCE_ENDPOINTS.ADD_NEW_CATEGORY, category.formData);
  }
  return apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_CATEGORY, category);
};
export const updateEcommerceCategory = (category) => {
  const categoryId = category.id;
  if (category.formData) {
    return apiClient.updateWithFormData(ECOMMERCE_ENDPOINTS.UPDATE_CATEGORY + '/' + categoryId, category.formData);
  }
  return apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_CATEGORY + '/' + categoryId, category);
};
export const deleteEcommerceCategory = (categoryId) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_CATEGORY + '/' + categoryId);

// Subcategories
export const getSubcategories = (params = {}) => apiClient.get(ECOMMERCE_ENDPOINTS.GET_SUBCATEGORIES, params);
export const addNewSubcategory = (subcategory) => apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_SUBCATEGORY, subcategory);
export const updateSubcategory = (subcategory) => apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_SUBCATEGORY + '/' + subcategory.id, subcategory);
export const deleteSubcategory = (subcategoryId) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_SUBCATEGORY + '/' + subcategoryId);

// Product Attributes
export const getAttributes = (params = {}) => apiClient.get(ECOMMERCE_ENDPOINTS.GET_ATTRIBUTES, params);
export const addNewAttribute = (attribute) => apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_ATTRIBUTE, attribute);
export const updateAttribute = (attribute) => apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_ATTRIBUTE + '/' + attribute.id, attribute);
export const deleteAttribute = (attributeId) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_ATTRIBUTE + '/' + attributeId);

// Commissions
export const getCommissions = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_COMMISSIONS);
export const addNewCommission = (commission) => apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_COMMISSION, commission);
export const updateCommission = (commission) => apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_COMMISSION + '/' + commission.id, commission);
export const deleteCommission = (commissionId) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_COMMISSION + '/' + commissionId);

// Taxes
export const getTaxes = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_TAXES);
export const addNewTax = (tax) => apiClient.create(ECOMMERCE_ENDPOINTS.ADD_NEW_TAX, tax);
export const updateTax = (tax) => apiClient.update(ECOMMERCE_ENDPOINTS.UPDATE_TAX + '/' + tax.id, tax);
export const deleteTax = (taxId) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_TAX + '/' + taxId);

// Home Banners (scroll banners on website home)
export const getHomeBanners = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_HOME_BANNERS);
export const addHomeBanner = (formData) => apiClient.createWithFormData(ECOMMERCE_ENDPOINTS.ADD_HOME_BANNER, formData);
export const deleteHomeBanner = (id) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_HOME_BANNER + '/' + id);

// Home Ad Banners (ad space on website home)
export const getHomeAdBanners = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_HOME_AD_BANNERS);
export const addHomeAdBanner = (formData) => apiClient.createWithFormData(ECOMMERCE_ENDPOINTS.ADD_HOME_AD_BANNER, formData);
export const deleteHomeAdBanner = (id) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_HOME_AD_BANNER + '/' + id);

// Footer Ad Banners
export const getFooterAdBanners = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_FOOTER_AD_BANNERS);
export const addFooterAdBanner = (formData) => apiClient.createWithFormData(ECOMMERCE_ENDPOINTS.ADD_FOOTER_AD_BANNER, formData);
export const deleteFooterAdBanner = (id) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_FOOTER_AD_BANNER + '/' + id);
export const updateFooterAdBanner = (id, formData) => apiClient.updateWithFormData(ECOMMERCE_ENDPOINTS.UPDATE_FOOTER_AD_BANNER + '/' + id, formData);

// Best Selling Ad Banners
export const getBestSellingAdBanners = () => apiClient.get(ECOMMERCE_ENDPOINTS.GET_BEST_SELLING_AD_BANNERS);
export const addBestSellingAdBanner = (formData) => apiClient.createWithFormData(ECOMMERCE_ENDPOINTS.ADD_BEST_SELLING_AD_BANNER, formData);
export const deleteBestSellingAdBanner = (id) => apiClient.delete(ECOMMERCE_ENDPOINTS.DELETE_BEST_SELLING_AD_BANNER + '/' + id);
export const updateBestSellingAdBanner = (id, formData) => apiClient.updateWithFormData(ECOMMERCE_ENDPOINTS.UPDATE_BEST_SELLING_AD_BANNER + '/' + id, formData);
